import { useEffect, useRef, useState, type ReactNode } from "react";
import type { Booking } from "@/types/domain";
import { usePendingBookings } from "@/hooks/usePendingBookings";
import { BookingPopupModal } from "@/features/bookings/BookingPopupModal";
import { useAppStore } from "@/store/app-store";
import { useBookingPopupStore } from "@/store/booking-popup-store";
import { bookingPopupKey, bookingResponseDeadline, isBookingResponseWindowOpen } from "@/lib/booking-window";
import { playAlertSound } from "@/lib/notification-sound";
import { bookingAlarm } from "@/lib/booking-alarm";
import { StayAuditPrompt } from "@/features/notifications/StayAuditPrompt";

// Same default as the backend / GoAdapter PMSInstance.alert_repeat_gap_minutes.
const DEFAULT_ALERT_REPEAT_GAP_MINUTES = 2;

export function NotificationProvider({ children }: { children: ReactNode }) {
  // Popups and sounds are for the property owner only - admins get live data but are never alerted.
  const isOwner = useAppStore((state) => state.user?.role === "OWNER");
  const ringUntilDismissed = useAppStore((state) => (state.user?.bookingAlertMode ?? "UNTIL_DISMISSED") === "UNTIL_DISMISSED");
  const pendingBookings = usePendingBookings();
  const dismissedIds = useBookingPopupStore((state) => state.dismissedIds);
  const dismiss = useBookingPopupStore((state) => state.dismiss);
  const knownIds = useRef<Set<string>>(new Set());
  const seeded = useRef(false);
  const ringing = useRef<Map<string, Booking>>(new Map());
  const [now, setNow] = useState(Date.now());
  const openBookings = (pendingBookings.data ?? []).filter((booking) => booking.bookingStatus === "PENDING" && isBookingResponseWindowOpen(booking, now));
  const pending = openBookings.filter((booking) => !dismissedIds.includes(bookingPopupKey(booking)));
  const activeBooking = pending[0] as Booking | undefined;
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const pendingSignature = pending.map(bookingPopupKey).join("|");

  useEffect(() => {
    if (!openBookings.length) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [openBookings.length]);

  useEffect(() => {
    if (!pendingBookings.data) return;
    const currentIds = new Set(pendingBookings.data.map(bookingPopupKey));
    const hasNewBooking = Array.from(currentIds).some((id) => !knownIds.current.has(id));
    // The first load only seeds what already existed; after that any new id rings - even when the
    // list was empty before (checking knownIds.size kept the first booking of a quiet spell silent).
    // In "until dismissed" mode the alarm below does the ringing instead.
    if (hasNewBooking && seeded.current && !ringUntilDismissed) playAlertSound();
    knownIds.current = currentIds;
    seeded.current = true;
  }, [pendingBookings.data, ringUntilDismissed]);

  // "Until dismissed": every request whose popup is still waiting rings; one leaving that list (answered, closed with X,
  // timed out) stops. Skipped while the list hasn't loaded so a failed fetch can't silence everything.
  useEffect(() => {
    if (!pendingBookings.data) return;
    const wanted = new Map<string, Booking>(isOwner && ringUntilDismissed ? pendingRef.current.map((booking) => [bookingPopupKey(booking), booking]) : []);
    // Stops first: a re-requested booking swaps its old key for a new one under the same booking id.
    for (const [key, booking] of ringing.current) {
      if (!wanted.has(key)) bookingAlarm.stop(booking.id, bookingResponseDeadline(booking));
    }
    for (const [key, booking] of wanted) {
      if (ringing.current.has(key)) continue;
      bookingAlarm.start({
        bookingId: booking.id,
        title: "New booking request",
        body: `${booking.bookingRef} is waiting for confirmation`,
        deadline: bookingResponseDeadline(booking),
        repeatGapMs: (booking.alertRepeatGapMinutes ?? DEFAULT_ALERT_REPEAT_GAP_MINUTES) * 60_000,
      });
    }
    ringing.current = wanted;
  }, [pendingSignature, pendingBookings.data, isOwner, ringUntilDismissed]);

  // Signed out or no longer an owner: nothing may keep ringing for the previous session.
  useEffect(() => {
    if (isOwner) return;
    ringing.current = new Map();
    bookingAlarm.clear();
  }, [isOwner]);

  return (
    <>
      {children}
      {isOwner && <StayAuditPrompt />}
      {isOwner && activeBooking && <BookingPopupModal booking={activeBooking} position={1} total={pending.length} onClose={() => dismiss(bookingPopupKey(activeBooking))} />}
    </>
  );
}
